import { supabase } from '../lib/supabase';

// ==================== DESAFIOS ====================

const getChallengeCreateErrorMessage = (message = '') => {
    if (message.includes('CHALLENGE_LIMIT_QUESTIONS')) {
        return 'Você precisa responder pelo menos 50 questões para criar um desafio.';
    }

    if (message.includes('CHALLENGE_LIMIT_WEEKLY')) {
        return 'Você já criou um desafio nos últimos 7 dias.';
    }

    return message;
};

/**
 * Obter todos os desafios ativos
 * Nota: Esta função usa a tabela 'challenges' antiga (desafios semanais)
 * Para os duelos 1v1, use os serviços em /features/Challenge
 */
export const getActiveChallenges = async () => {
    try {
        const { data, error } = await supabase
            .from('challenges')
            .select('id, titulo, descricao, tipo, disciplina, assunto, meta_questoes, premio_xp, data_inicio, data_fim, ativo, icone')
            .eq('ativo', true)
            .gte('data_fim', new Date().toISOString())
            .order('data_fim', { ascending: true })
            .limit(20); // Limite para performance

        if (error) {
            // Silently handle table/column not found errors
            if (error.message?.includes('does not exist') || error.code === '42703' || error.code === '42P01') {

                return [];
            }
            throw error;
        }
        return data || [];
    } catch (error) {
        // Don't spam console with expected errors
        if (!error.message?.includes('does not exist')) {

        }
        return [];
    }
};

/**
 * Obter desafios por tipo (daily, weekly, special)
 */
export const getChallengesByType = async (tipo) => {
    try {
        const { data, error } = await supabase
            .from('challenges')
            .select('*')
            .eq('ativo', true)
            .eq('tipo', tipo)
            .gte('data_fim', new Date().toISOString())
            .order('data_fim', { ascending: true });

        if (error) throw error;
        return data || [];
    } catch (error) {

        return [];
    }
};

/**
 * Participar de um desafio
 */
export const joinChallenge = async (challengeId, userId) => {
    try {
        const { data, error } = await supabase
            .from('challenge_participants')
            .insert({
                challenge_id: challengeId,
                user_id: userId,
            })
            .select()
            .single();

        if (error) {
            if (error.code === '23505') {
                // Já está participando
                return { success: true, alreadyJoined: true };
            }
            throw error;
        }
        return { success: true, data };
    } catch (error) {

        return { success: false, error: error.message };
    }
};

/**
 * Atualizar progresso no desafio
 */
export const updateChallengeProgress = async (challengeId, userId, isCorrect) => {
    try {
        // Buscar participação atual
        const { data: current, error: fetchError } = await supabase
            .from('challenge_participants')
            .select('id, questoes_respondidas, questoes_corretas, score')
            .eq('challenge_id', challengeId)
            .eq('user_id', userId)
            .single();

        if (fetchError) throw fetchError;

        // Atualizar progresso
        const newData = {
            questoes_respondidas: current.questoes_respondidas + 1,
            questoes_corretas: current.questoes_corretas + (isCorrect ? 1 : 0),
            score: current.score + (isCorrect ? 10 : 0), // 10 pontos por acerto
        };

        const { data, error } = await supabase
            .from('challenge_participants')
            .update(newData)
            .eq('id', current.id)
            .select()
            .single();

        if (error) throw error;
        return { success: true, data };
    } catch (error) {

        return { success: false, error: error.message };
    }
};

/**
 * Obter ranking de um desafio
 */
export const getChallengeRanking = async (challengeId, limit = 50) => {
    try {
        const { data, error } = await supabase
            .from('challenge_participants')
            .select(`
        *,
        profiles:user_id (nome, foto_url, nivel)
      `)
            .eq('challenge_id', challengeId)
            .order('score', { ascending: false })
            .limit(limit);

        if (error) throw error;
        return data || [];
    } catch (error) {

        return [];
    }
};

/**
 * Obter minha participação em um desafio
 */
export const getMyParticipation = async (challengeId, userId) => {
    try {
        const { data, error } = await supabase
            .from('challenge_participants')
            .select('id, challenge_id, user_id, score, questoes_respondidas, questoes_corretas, joined_at')
            .eq('challenge_id', challengeId)
            .eq('user_id', userId)
            .single();

        if (error && error.code !== 'PGRST116') throw error; // PGRST116 = not found
        return data;
    } catch (error) {

        return null;
    }
};

/**
 * Obter contagem de participantes de um desafio
 */
export const getChallengeParticipantsCount = async (challengeId) => {
    try {
        // Otimizado: contagem local em vez de HEAD
        const { data, error } = await supabase
            .from('challenge_participants')
            .select('id')
            .eq('challenge_id', challengeId)
            .limit(100); // Limite otimizado (reduzido de 500)

        if (error) throw error;
        return data?.length || 0;
    } catch (error) {

        return 0;
    }
};

// Alias para compatibilidade
export const getUserParticipation = getMyParticipation;

/**
 * OTIMIZADO: Obter desafios ativos com contagem de participantes e participação do usuário
 * Usa 2 queries paralelas (mais confiável que JOIN sem FK)
 * 
 * @param {string} userId - ID do usuário para verificar participação
 * @returns {Promise<Array>} Desafios com participants e isParticipating
 */
export const getActiveChallengesOptimized = async (userId = null) => {
    try {
        // 2 queries em paralelo (ainda muito mais rápido que N+1)
        const [challengesResult, participantsResult] = await Promise.all([
            supabase
                .from('challenges')
                .select('id, titulo, descricao, tipo, disciplina, assunto, meta_questoes, premio_xp, data_inicio, data_fim, ativo, icone')
                .eq('ativo', true)
                .gte('data_fim', new Date().toISOString())
                .order('data_fim', { ascending: true })
                .limit(20), // Limite de desafios
            supabase
                .from('challenge_participants')
                .select('challenge_id, user_id, score, questoes_respondidas, questoes_corretas')
                .limit(100) // Limite otimizado (reduzido de 500)
        ]);

        // Handle missing table/column errors silently
        if (challengesResult.error) {
            if (challengesResult.error.message?.includes('does not exist') ||
                challengesResult.error.code === '42703' ||
                challengesResult.error.code === '42P01') {

                return [];
            }

            return [];
        }

        if (participantsResult.error) {
            // Participantes é opcional, continua mesmo com erro

        }

        const challenges = challengesResult.data || [];
        const allParticipants = participantsResult.data || [];


        // Processar dados localmente (muito mais rápido que N queries)
        const processedData = challenges.map(challenge => {
            const participants = allParticipants.filter(p => p.challenge_id === challenge.id);
            const myParticipation = userId
                ? participants.find(p => p.user_id === userId)
                : null;

            const topScore = participants.length > 0
                ? Math.max(...participants.map(p => p.score || 0))
                : 0;

            // Calcular tempo restante
            const dataFim = new Date(challenge.data_fim);
            const agora = new Date();
            const diffMs = dataFim - agora;
            const diffHoras = Math.floor(diffMs / (1000 * 60 * 60));
            const diffMinutos = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
            const timeRemaining = diffMs > 0 ? `${diffHoras}h ${diffMinutos}min` : 'Encerrado';

            // Cores padrão por tipo
            const defaultGradients = {
                daily: ['#3B82F6', '#1D4ED8'],
                weekly: ['#10B981', '#059669'],
                special: ['#F59E0B', '#D97706'],
            };

            return {
                ...challenge,
                participants: participants.length,
                isParticipating: !!myParticipation,
                myProgress: myParticipation?.questoes_respondidas || 0,
                myScore: myParticipation?.score || 0,
                topScore: topScore,
                // Campos UI com defaults (se não existirem no DB)
                gradientColors: challenge.gradient_colors || defaultGradients[challenge.tipo] || ['#3B82F6', '#1D4ED8'],
                icon: challenge.icone || (challenge.tipo === 'daily' ? '⚡' : '📅'),
                color: challenge.cor || '#3B82F6',
                timeRemaining: timeRemaining,
                prize: challenge.premio || '500 XP',
                prizeIcon: challenge.premio_icone || '✨',
                type: challenge.tipo || 'daily',
                title: challenge.titulo || challenge.title,
                subject: challenge.disciplina || challenge.subject || 'Geral',
                description: challenge.descricao || challenge.description || '',
            };
        });

        return processedData;
    } catch (error) {

        return [];
    }
};

// ==================== CRIAR DESAFIO DE USUÁRIO ====================

/**
 * Verifica se o usuário pode criar um desafio
 * Requisitos: 50+ questões respondidas E máximo 1 desafio por semana
 * 
 * @param {string} userId - ID do usuário
 * @returns {Promise<{canCreate: boolean, reason: string, questionsAnswered: number}>}
 */
export const canUserCreateChallenge = async (userId) => {
    try {
        if (!userId) return { canCreate: false, reason: 'Usuário não autenticado', questionsAnswered: 0 };

        // 1. Verificar total de questões respondidas
        const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('questoes_respondidas')
            .eq('id', userId)
            .single();

        // Handle missing table/column gracefully
        if (profileError) {
            if (profileError.message?.includes('does not exist') || profileError.code === 'PGRST116') {
                // Table/column doesn't exist or no profile found - allow creation
                return { canCreate: true, reason: 'Elegível para criar desafio', questionsAnswered: 0 };
            }
            throw profileError;
        }

        const questionsAnswered = profile?.questoes_respondidas || 0;

        if (questionsAnswered < 50) {
            return {
                canCreate: false,
                reason: `Faltam ${50 - questionsAnswered} questões para desbloquear`,
                questionsAnswered
            };
        }

        // 2. Verificar se já criou desafio esta semana
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

        try {
            const { data: recentChallenges, error: challengeError } = await supabase
                .from('challenges')
                .select('id, created_at')
                .eq('criado_por', userId)
                .gte('created_at', oneWeekAgo.toISOString());

            // If column doesn't exist, just allow creation
            if (challengeError) {
                if (challengeError.message?.includes('does not exist')) {
                    return { canCreate: true, reason: 'Elegível para criar desafio', questionsAnswered };
                }
                throw challengeError;
            }

            if (recentChallenges && recentChallenges.length > 0) {
                return {
                    canCreate: false,
                    reason: 'Você já criou um desafio esta semana',
                    questionsAnswered
                };
            }
        } catch (innerError) {
            // If challenges table has different structure, allow creation
            if (innerError.message?.includes('does not exist')) {
                return { canCreate: true, reason: 'Elegível para criar desafio', questionsAnswered };
            }
            throw innerError;
        }

        return { canCreate: true, reason: 'Elegível para criar desafio', questionsAnswered };
    } catch (error) {
        // Don't log expected errors
        if (!error.message?.includes('does not exist')) {

        }
        // On error, allow creation to not block the user
        return { canCreate: true, reason: 'Elegível para criar desafio', questionsAnswered: 0 };
    }
};

/**
 * Cria um novo desafio de usuário
 * 
 * NOTA: Esta função requer a tabela 'challenges' antiga (desafios semanais).
 * Se a tabela foi migrada para o sistema de Duelos 1v1, esta função não funcionará.
 * 
 * @param {object} challengeData - Dados do desafio
 * @param {string} challengeData.titulo - Título do desafio
 * @param {string} challengeData.descricao - Descrição
 * @param {string} challengeData.disciplina - Disciplina (INFORMATICA, PORTUGUES, etc.)
 * @param {string} challengeData.assunto - Assunto específico (opcional)
 * @param {string} challengeData.icone - Emoji do desafio
 * @param {string[]} challengeData.gradientColors - Cores do gradiente
 * @param {string} userId - ID do criador
 * @returns {Promise<{success: boolean, challenge?: object, error?: string}>}
 */
export const createUserChallenge = async (challengeData, userId) => {
    try {
        if (!userId) return { success: false, error: 'Usuário não autenticado' };

        // Verificar elegibilidade novamente
        const eligibility = await canUserCreateChallenge(userId);
        if (!eligibility.canCreate) {
            return { success: false, error: eligibility.reason };
        }

        // Configurar datas (7 dias)
        const dataInicio = new Date();
        const dataFim = new Date();
        dataFim.setDate(dataFim.getDate() + 7);

        // Criar desafio
        const { data, error } = await supabase
            .from('challenges')
            .insert({
                titulo: challengeData.titulo,
                descricao: challengeData.descricao || `Desafio de ${challengeData.disciplina}`,
                tipo: 'user', // Tipo específico para desafios de usuário
                disciplina: challengeData.disciplina,
                assunto: challengeData.assunto || null,
                meta_questoes: 50, // Meta padrão
                premio_xp: 1000, // XP fixo para desafios de usuário
                data_inicio: dataInicio.toISOString(),
                data_fim: dataFim.toISOString(),
                ativo: true,
                criado_por: userId,
                icone: challengeData.icone || '🎯',
                gradient_colors: challengeData.gradientColors || ['#8B5CF6', '#6D28D9'],
            })
            .select()
            .single();

        if (error) {
            // Handle schema mismatch errors
            if (error.message?.includes('does not exist') ||
                error.message?.includes('schema cache') ||
                error.message?.includes('column')) {

                return {
                    success: false,
                    error: 'Desafios semanais temporariamente indisponíveis. Tente novamente mais tarde.'
                };
            }
            return { success: false, error: getChallengeCreateErrorMessage(error.message) };
        }

        // Criar participação automática do criador
        await joinChallenge(data.id, userId);

        return { success: true, challenge: data };
    } catch (error) {
        // Handle schema mismatch errors gracefully
        if (error.message?.includes('does not exist') ||
            error.message?.includes('schema cache') ||
            error.message?.includes('column')) {
            return {
                success: false,
                error: 'Desafios semanais temporariamente indisponíveis. Tente novamente mais tarde.'
            };
        }

        return { success: false, error: getChallengeCreateErrorMessage(error.message) };
    }
};

export default {
    getActiveChallenges,
    getActiveChallengesOptimized,
    getChallengesByType,
    joinChallenge,
    updateChallengeProgress,
    getChallengeRanking,
    getMyParticipation,
    getUserParticipation,
    getChallengeParticipantsCount,
    canUserCreateChallenge,
    createUserChallenge,
};
